import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBusiness } from '../context/BusinessContext';
import { useModal } from '../context/ModalContext';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../api/client';

interface TeamMember {
  id: string;
  email: string;
  role: string;
  created_at: string;
  invited_at?: string;
  accepted_at?: string;
  invited_by_email?: string;
}

export default function TeamScreen() {
  const { user, isSuperAdmin } = useAuth();
  const { needsVenueSelection } = useBusiness();
  const { showAlert, showDanger } = useModal();
  const navigate = useNavigate();

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    role: 'VENUE_ADMIN' as 'VENUE_ADMIN' | 'VENUE_STAFF',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Redirect SUPERADMIN to venue selection if needed
  useEffect(() => {
    if (needsVenueSelection && isSuperAdmin) {
      navigate('/superadmin');
    }
  }, [needsVenueSelection, isSuperAdmin, navigate]);

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getTeamMembers();
      setMembers(response.admins);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleAddMember = async () => {
    setFormError(null);

    if (!formData.email) {
      setFormError('Email is required');
      return;
    }

    try {
      setSubmitting(true);
      await api.createTeamMember({
        email: formData.email,
        role: formData.role,
      });
      setShowAddModal(false);
      setFormData({ email: '', role: 'VENUE_ADMIN' });
      fetchMembers();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to add team member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveMember = async (member: TeamMember) => {
    if (member.id === user?.id) {
      await showAlert('Cannot Remove', 'You cannot remove yourself from the team.', 'warning');
      return;
    }

    if (member.role === 'SUPERADMIN') {
      await showAlert('Cannot Remove', 'Super Admin accounts cannot be removed.', 'warning');
      return;
    }

    const confirmed = await showDanger(
      'Remove Team Member',
      `Are you sure you want to remove ${member.email} from your team? They will lose access to this venue.`,
      'Remove'
    );

    if (!confirmed) return;

    try {
      await api.removeTeamMember(member.id);
      fetchMembers();
    } catch (err) {
      await showAlert('Error', err instanceof Error ? err.message : 'Failed to remove team member', 'error');
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'SUPERADMIN':
        return { label: 'Super Admin', className: 'badge-primary' };
      case 'VENUE_ADMIN':
        return { label: 'Admin', className: 'badge-success' };
      case 'VENUE_STAFF':
        return { label: 'Staff', className: 'badge-warning' };
      default:
        return { label: role, className: '' };
    }
  };

  if (needsVenueSelection) {
    return (
      <DashboardLayout>
        <div className="page-header">
          <h1>Select a Venue</h1>
          <p>As a Super Admin, please select a venue to manage from the All Venues page.</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/superadmin')}>
          Go to All Venues
        </button>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-header">
        <div>
          <h1>Team</h1>
          <p>Manage who has access to your venue dashboard</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
          + Add Team Member
        </button>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '24px' }}>
          {error}
        </div>
      )}

      <div className="card">
        {loading ? (
          <div className="loading" style={{ minHeight: '200px' }}>
            <div className="spinner"></div>
          </div>
        ) : members.filter(m => m.role !== 'SUPERADMIN').length === 0 ? (
          <div className="empty-state">
            <h3>No team members yet</h3>
            <p>Add team members to give them access to manage your venue</p>
            <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
              + Add Team Member
            </button>
          </div>
        ) : (
          <table className="pricing-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Added</th>
                <th style={{ width: '100px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.filter(m => m.role !== 'SUPERADMIN').map((member) => {
                const badge = getRoleBadge(member.role);
                const isCurrentUser = member.id === user?.id;
                return (
                  <tr key={member.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span>{member.email}</span>
                        {isCurrentUser && (
                          <span style={{
                            fontSize: '11px',
                            color: 'var(--gray-500)',
                            fontStyle: 'italic'
                          }}>(you)</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${badge.className}`}>{badge.label}</span>
                    </td>
                    <td style={{ color: 'var(--gray-500)', fontSize: '14px' }}>
                      {new Date(member.created_at).toLocaleDateString()}
                      {member.invited_by_email && (
                        <div style={{ fontSize: '12px', color: 'var(--gray-400)' }}>
                          by {member.invited_by_email}
                        </div>
                      )}
                    </td>
                    <td>
                      {!isCurrentUser && member.role !== 'SUPERADMIN' && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleRemoveMember(member)}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Info Card */}
      <div style={{ marginTop: '24px', padding: '16px', background: 'var(--gray-100)', borderRadius: '8px' }}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: 'var(--gray-700)' }}>About Roles</h4>
        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: 'var(--gray-600)' }}>
          <li><strong>Admin</strong> - Full access to manage courts, bookings, settings, and team members</li>
          <li><strong>Staff</strong> - Can view and manage bookings only (coming soon)</li>
        </ul>
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Team Member</h2>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>x</button>
            </div>
            <div className="modal-body">
              {formError && (
                <div className="alert alert-error" style={{ marginBottom: '16px' }}>
                  {formError}
                </div>
              )}
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  placeholder="teammate@example.com"
                />
              </div>
              <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: 'var(--gray-500)' }}>
                An invite will be sent with a link to set their password.
              </p>
              <div className="form-group">
                <label>Role</label>
                <select
                  value={formData.role}
                  onChange={e => setFormData({ ...formData, role: e.target.value as 'VENUE_ADMIN' | 'VENUE_STAFF' })}
                >
                  <option value="VENUE_ADMIN">Admin - Full access</option>
                  <option value="VENUE_STAFF">Staff - Bookings only</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddMember}
                disabled={submitting}
              >
                {submitting ? 'Adding...' : 'Add Team Member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
